import { apiErrorSchema } from '@cms/shared';
import type { ZodType } from 'zod';

/**
 * The single door to the API (§5.3, `lib/`).
 *
 * Two things are deliberate here:
 *
 * 1. Every response is parsed through the caller's zod schema. The front end never
 *    hand-writes a response type — it imports the contract from @cms/shared, so a
 *    back-end shape change fails loudly at the boundary instead of rendering undefined
 *    three components deep (§3.2, "untyped service boundaries").
 * 2. A 401 triggers one refresh attempt and one retry, and concurrent 401s share a
 *    single in-flight refresh. Phase 1 supplies the real refresh call; the machinery
 *    lives here now so no call site ever grows its own retry logic (§6.5).
 */

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/** Set by the auth provider in Phase 1. Until then the app has no session to refresh. */
type RefreshFn = () => Promise<boolean>;
let refresh: RefreshFn = async () => false;
export function setRefreshHandler(fn: RefreshFn): void {
  refresh = fn;
}

/** Concurrent 401s must not fire N refreshes; they all await the same one. */
let inFlightRefresh: Promise<boolean> | null = null;
function refreshOnce(): Promise<boolean> {
  inFlightRefresh ??= refresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/**
 * The access token is held in memory only — never localStorage, which is readable by
 * any XSS payload. The refresh token lives in an httpOnly cookie the JS never sees (§6.5).
 */
let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function raw(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  return fetch(`/api/v1${path}`, {
    method: options.method ?? 'GET',
    headers,
    // Sends the refresh cookie; same-origin so this is not a CORS credential request.
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

async function toError(response: Response): Promise<ApiClientError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return new ApiClientError('UNKNOWN', response.statusText, response.status);
  }
  const parsed = apiErrorSchema.safeParse(payload);
  if (!parsed.success) {
    return new ApiClientError('UNKNOWN', response.statusText, response.status);
  }
  const { code, message, requestId, details } = parsed.data.error;
  // requestId is what ties a red toast to a log line; carry it or debugging is guesswork.
  return new ApiClientError(code, message, response.status, requestId, details);
}

/**
 * The same request, returning the RESPONSE rather than a parsed contract.
 *
 * ── Why this exists alongside `apiRequest` ────────────────────────────────────────────
 * Exactly one thing this API returns is not JSON through a zod contract: an export's bytes. Parsing those
 * through a schema is meaningless, and fetching them with bare `fetch` would lose the two behaviours that
 * matter — the Authorization header, and the single transparent retry after a 401 that makes an expired
 * access token invisible to the caller. So this shares all of that and hands back the response for the
 * caller to read as a blob.
 *
 * Errors are still turned into `ApiClientError`, so a 403 on an export reads like a 403 anywhere else.
 */
export async function apiRequestRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  let response = await raw(path, options);

  if (response.status === 401 && (await refreshOnce())) {
    response = await raw(path, options);
  }

  if (!response.ok) throw await toError(response);
  return response;
}

export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  let response = await raw(path, options);

  if (response.status === 401 && (await refreshOnce())) {
    response = await raw(path, options);
  }

  if (!response.ok) throw await toError(response);
  if (response.status === 204) return schema.parse(undefined);

  return schema.parse(await response.json());
}
